import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import FeaturedHero from "@/components/FeaturedHero";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FALLBACK_TITLES = {
  sarees: "Sarees", lehengas: "Party Wear", kurtis: "Kurtis", dresses: "Dresses",
  new: "New Arrivals", bestsellers: "Bestsellers", all: "All Pieces",
};

const BANNERS = {
  new: "https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/r33gxyjm_971749ba-d468-4660-81c3-1a0f2a37adc6.png",
  bestsellers: "https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/ahzjxman_7113effc-b39e-4482-a805-ae09d76f5536.JPG",
  all: "https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/6uttp3y2_IMG_2513%202.PNG",
};

const REAL_CATEGORIES = ["sarees", "kurtis", "dresses", "lehengas"];

export default function Shop({ mode }) {
  const params = useParams();
  const slug = mode || params.category || "all";
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("default");

  // Fetch admin-editable category settings for real categories.
  useEffect(() => {
    if (!REAL_CATEGORIES.includes(slug)) { setSettings(null); return; }
    api.get(`/categories/${slug}`)
      .then(({ data }) => setSettings(data))
      .catch((err) => {
        console.error("Failed to load category settings:", err);
        setSettings(null);
      });
  }, [slug]);

  // Fetch products.
  useEffect(() => {
    setLoading(true);
    const query = {};
    if (slug === "new") query.tag = "new";
    else if (slug === "bestsellers") query.tag = "bestseller";
    else if (slug !== "all") query.category = slug;
    api.get("/products", { params: query })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [slug]);

  // Sort + apply max_products cap (if admin set one).
  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price-asc") arr.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") arr.sort((a, b) => b.price - a.price);
    else if (sort === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
    // Default sort comes from backend (newest first); apply max_products cap only
    // when the admin has set a positive limit.
    const cap = settings?.max_products;
    if (sort === "default" && cap && cap > 0) return arr.slice(0, cap);
    return arr;
  }, [products, sort, settings]);

  const title = settings?.title || FALLBACK_TITLES[slug] || "Shop";
  const description = settings?.description || "";

  return (
    <div>
      {/* For real categories, the FeaturedHero IS the page hero — it carries the category title + featured product in one branded composition. */}
      {REAL_CATEGORIES.includes(slug) ? (
        <FeaturedHero
          category={slug}
          heading={title}
          eyebrow={settings?.eyebrow}
          description={description}
        />
      ) : (
        <section className="relative h-[46vh] min-h-[340px] overflow-hidden" data-testid="shop-banner">
          <img src={BANNERS[slug] || BANNERS.all} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7]/20 to-transparent" />
          <div className="absolute inset-0 flex items-end px-6 md:px-12 lg:px-24 pb-10">
            <div>
              <div className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-3">The Collection</div>
              <h1 className="font-serif-h font-light text-5xl md:text-6xl text-stone-900">{title}</h1>
            </div>
          </div>
        </section>
      )}

      <section className="px-6 md:px-12 lg:px-24 py-14">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div className="text-sm text-stone-500" data-testid="shop-count">
            {sorted.length} {sorted.length === 1 ? "piece" : "pieces"}
            {settings?.max_products && products.length > sorted.length && (
              <span className="text-stone-400"> · Showing latest {settings.max_products} of {products.length}</span>
            )}
          </div>
          <div className="w-48">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="border-warm rounded-sm"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Featured</SelectItem>
                <SelectItem value="price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price-desc">Price: High to Low</SelectItem>
                <SelectItem value="name">Name: A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[...Array(8)].map((_, i) => (
              <div key={`skeleton-${i}`} className="aspect-[3/4] bg-[#F3EAE8] animate-pulse" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-24 text-stone-500">No pieces found in this edit.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-testid="shop-grid">
            {sorted.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
